import { useAuthContext } from '@/auth/useAuthContext';
import { ApiError, issueCustomerCredit } from '@/lib/api';
import { X } from 'lucide-react';
import type { FormEvent } from 'react';
import { useState } from 'react';

interface IssueCreditDrawerProps {
  customerId: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful issuance so the parent can refresh. */
  onIssued: () => void;
}

/**
 * Modal drawer for issuing a goodwill / refund-as-credit balance to a
 * customer. Capability-gated server-side by `customers.credit_issue`; the
 * UI does not pre-gate (server returns 403 if a non-owner/non-manager
 * somehow opens the drawer).
 *
 * Amount input is in MAJOR currency units (e.g. "12.50"). The component
 * floors to minor units before POSTing — never rounds up; pricing engine
 * does the same for the same reason (never steal pence).
 */
export function IssueCreditDrawer({ customerId, open, onClose, onIssued }: IssueCreditDrawerProps) {
  const { state } = useAuthContext();
  const currency = state.me?.activeAccount?.currency ?? 'GBP';

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState(''); // ISO date input value
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function reset() {
    setAmount('');
    setReason('');
    setExpiresAt('');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Major → minor.
    const parsed = Number.parseFloat(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    const amountMinor = Math.round(parsed * 100);
    if (amountMinor <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    if (reason.trim().length === 0) {
      setError('Reason is required');
      return;
    }

    setSaving(true);
    try {
      // Body shape: include expiresAt only when user typed one. Empty
      // means "use the account default" — which the server computes.
      const body: { amountMinor: number; reason: string; expiresAt?: string } = {
        amountMinor,
        reason: reason.trim(),
      };
      if (expiresAt.length > 0) {
        // Date input is YYYY-MM-DD; promote to end-of-day UTC so a credit
        // issued "expires 2026-12-31" remains valid through that day.
        body.expiresAt = `${expiresAt}T23:59:59.000Z`;
      }
      await issueCustomerCredit(customerId, body);
      reset();
      onIssued();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setError('You do not have permission to issue credits.');
        } else if (err.status === 400) {
          const body = err.body as { issues?: string[] } | null;
          setError(body?.issues?.join(', ') ?? 'Invalid input');
        } else {
          setError(`Failed to issue credit (${err.status})`);
        }
      } else {
        setError((err as Error).message ?? 'Failed to issue credit');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Issue credit"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-4 rounded-2xl bg-surface p-6 ring-1 ring-border elevate"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Issue credit</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-secondary press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div>
          <label
            htmlFor="credit-amount"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Amount ({currency})
          </label>
          <input
            id="credit-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={saving}
            placeholder="0.00"
            className="num h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        <div>
          <label
            htmlFor="credit-reason"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Reason
          </label>
          <input
            id="credit-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
            placeholder="e.g. Late dropoff apology"
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        <div>
          <label
            htmlFor="credit-expires"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Expires
          </label>
          <input
            id="credit-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            disabled={saving}
            className="num h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Leave blank to use the account default.
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
          >
            {saving ? 'Issuing…' : 'Issue credit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-secondary press disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
